import { Component, Input, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionsService } from 'core/services/subscriptions.service';
import { Client } from 'core/models/client';
import { StoreService } from 'core/services/store.service';
import { DateTime } from 'luxon';
import { ConfigurationsService, LocalesService } from 'core/services';
import { ChartDataset } from 'chart.js';

@Component({
  selector: 'lms-client-credits',
  templateUrl: './client-credits.component.html',
  styleUrls: ['./client-credits.component.scss']
})
export class ClientCreditsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;
  @Input() updatedCredits: boolean;
  @Input() listId: string;
  public listSubscription: Subscription;
  public credits: any;
  public data: any;
  public noCreditsBags: boolean;
  public allCreditsExpired: boolean;
  public allCreditsConsumed: boolean;
  public noValidCredits: boolean;
  public chartData: ChartDataset[] | any[];
  public chartLabels: string[] = [];
  public chartColors: { backgroundColor?: string[]; borderColor?: string[] } = {
    backgroundColor: ['#007eff', '#50B3DF', '#A7E4FF']
  };
  public chartKeys = ['available', 'consumed'];
  public chartLegendColorClasses = [];
  public tooltipsText: string;
  public chartOptions = { height: 200 };
  private tooltipSubscription: Subscription = new Subscription();

  constructor(
    public subscriptionsService: SubscriptionsService,
    public translate: TranslateService,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService
  ) {}

  ngOnInit(): void {
    this.listSubscription = StoreService.get(this.listId).subscribe((dataSource) => {
      if (dataSource) {
        this.setCollection(dataSource.docs);
      }
    });

    this.chartLabels = [
      this.translate.instant('clients.credits.available'),
      this.translate.instant('clients.credits.consumed')
    ];
    this.chartColors = { backgroundColor: ['#007eff', '#50B3DF', '#A7E4FF'] };
    this.chartLegendColorClasses = ['color-game-8', 'color-game-11', 'color-game-12'];

    if (!StoreService.contains('client.credits')) {
      this.tooltipSubscription = this.configurationsService
        .getInfoMessage$(
          'toolTip',
          'fields.keys[match]',
          'client.credits',
          'textoDeAyuda',
          this.localesService.getBrowserLocale()
        )
        .subscribe((tooltipsMap) => {
          this.tooltipsText = tooltipsMap;
        });
    }
  }

  ngOnChanges(): void {
    this.loadClientCreditsResume();
  }

  ngOnDestroy(): void {
    this.listSubscription.unsubscribe();
    this.tooltipSubscription.unsubscribe();
  }

  setCollection(subscriptions): void {
    this.noCreditsBags = !subscriptions || !subscriptions.length;
    this.allCreditsExpired = false;
    this.data = subscriptions;
    if (subscriptions && subscriptions.length > 0) {
      const today = DateTime.local();
      this.allCreditsExpired = subscriptions.every((bag) => today > DateTime.fromISO(bag.endDate));
    }
  }

  loadClientCreditsResume(): void {
    if (this.client) {
      this.subscriptionsService.getClientCreditsResume(this.client.id).then((credits) => {
        this.credits = credits || 0;
        this.noValidCredits = this.credits.total === 0;
        this.allCreditsConsumed = this.credits.total > 0 && this.credits.total === this.credits.consumed;

        this.chartData = [this.credits.available, this.credits.consumed];
      });
    }
  }

  get available() {
    return !(this.noValidCredits || this.allCreditsExpired || this.noCreditsBags || this.allCreditsConsumed);
  }
}
